import { useRouter } from '@tanstack/react-router';
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { anchorFrom, useAnchoredPanel, type AnchorRect } from '../lib/anchored-panel';
import styles from './Popover.module.css';

// Anchored pop-ups — menus, flyouts, pickers. CLAUDE.md §5: they open next to
// their trigger, never centred on screen.
//
// 06-qol §2: closes on Esc, and focus returns to the element that opened it.
// Portaled to <body> so a panel is never clipped by an ancestor's `overflow`.

interface UsePopoverResult<TTrigger extends HTMLElement> {
  readonly isOpen: boolean;
  readonly anchor: AnchorRect | null;
  readonly triggerRef: RefObject<TTrigger | null>;
  open(): void;
  close(): void;
  toggle(): void;
}

/** The trigger is a button, unless it can't be one (a multi-select holds the chips' own buttons). */
export function usePopover<TTrigger extends HTMLElement = HTMLButtonElement>(): UsePopoverResult<TTrigger> {
  const triggerRef = useRef<TTrigger | null>(null);
  const [anchor, setAnchor] = useState<AnchorRect | null>(null);

  const open = useCallback(() => {
    if (triggerRef.current) setAnchor(anchorFrom(triggerRef.current));
  }, []);

  const close = useCallback(() => {
    setAnchor(null);
    // Focus returns to the trigger, always (06-qol §2). Without this the keyboard
    // user is dropped back at the top of the document.
    triggerRef.current?.focus();
  }, []);

  const toggle = useCallback(() => {
    setAnchor((current) => (current ? null : triggerRef.current ? anchorFrom(triggerRef.current) : null));
  }, []);

  return { isOpen: anchor !== null, anchor, triggerRef, open, close, toggle };
}

interface PopoverProps {
  readonly anchor: AnchorRect | null;
  readonly onClose: () => void;
  readonly align?: 'left' | 'right';
  readonly width?: number;
  readonly label: string;
  readonly children: ReactNode;
}

/**
 * The pop-ups open right now, oldest first. Esc closes only the newest: a date picker opened
 * inside the publish panel closes alone, and the panel stays (S32). Every pop-up listens on the
 * window, so stopping propagation cannot decide this — the order of opening does.
 */
const openPopovers: string[] = [];

export function Popover({
  anchor,
  onClose,
  align = 'left',
  width,
  label,
  children,
}: PopoverProps): ReactNode {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const style = useAnchoredPanel(anchor, panelRef, align, width);
  const id = useId();

  useEffect(() => {
    if (!anchor) return;

    openPopovers.push(id);
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape' || openPopovers.at(-1) !== id) return;
      event.stopPropagation();
      onClose();
    };
    // Pointer-down rather than click: a click that started inside and finished
    // outside (a drag on a slider, a text selection) should not close the panel.
    const onPointerDown = (event: PointerEvent): void => {
      if (panelRef.current?.contains(event.target as Node)) return;
      // A click inside another pop-up is a click inside one opened FROM this one —
      // a Select in a filter menu. Its list is portaled to <body>, so it is outside
      // this panel in the DOM, and closing here would unmount the Select mid-click
      // and throw the choice away.
      if (event.target instanceof Element && event.target.closest('[data-popover]')) return;
      onClose();
    };

    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      openPopovers.splice(openPopovers.indexOf(id), 1);
      window.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [anchor, onClose, id]);

  if (!anchor) return null;

  return createPortal(
    <div
      ref={panelRef}
      id={id}
      className={styles.panel}
      style={style}
      data-popover
      role="dialog"
      aria-label={label}
    >
      {children}
    </div>,
    document.body,
  );
}

interface MenuItemProps {
  readonly icon?: ReactNode;
  readonly meta?: ReactNode;
  readonly isSelected?: boolean;
  readonly isDanger?: boolean;
  readonly disabled?: boolean;
  readonly onClick?: () => void;
  readonly href?: string;
  readonly children: ReactNode;
}

export function MenuItem({
  icon,
  meta,
  isSelected = false,
  isDanger = false,
  disabled = false,
  onClick,
  href,
  children,
}: MenuItemProps): ReactNode {
  const router = useRouter();
  const classes = [styles.item, isSelected ? styles.itemSelected : '', isDanger ? styles.itemDanger : '']
    .filter(Boolean)
    .join(' ');

  // A plain left click stays inside the app (CLAUDE.md §5a rule 7); anything else — a middle
  // click, Ctrl-click — is the browser's, so "open in a new tab" still works.
  const followLink = (event: MouseEvent<HTMLAnchorElement>): void => {
    onClick?.();
    if (href === undefined || event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    router.history.push(href);
  };

  const content = (
    <>
      {icon && <span className={styles.itemIcon}>{icon}</span>}
      <span className={styles.itemLabel}>{children}</span>
      {meta && <span className={styles.itemMeta}>{meta}</span>}
    </>
  );

  // A real anchor when it navigates, so open-in-new-tab works (06-qol §8).
  if (href !== undefined) {
    return (
      <a className={classes} href={href} onClick={followLink}>
        {content}
      </a>
    );
  }

  return (
    <button type="button" className={classes} onClick={onClick} disabled={disabled}>
      {content}
    </button>
  );
}

export function MenuGroup({ label, children }: { label?: string; children: ReactNode }): ReactNode {
  return (
    <div className={styles.group}>
      {label !== undefined && <span className={styles.groupLabel}>{label}</span>}
      {children}
    </div>
  );
}

export function MenuSeparator(): ReactNode {
  return <div className={styles.separator} role="separator" />;
}
